import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  competenceTotal, endpoints, readoutFor,
  type AttributeCatalog, type AttributeDefinition, type BoardFinding, type BoardMember,
  type BoardValidation, type PersonaAttributes, type PersonaPreset,
} from '../api/client'
import { Avatar, ramp } from '../components/Avatar'
import { Fingerprint, FingerprintLarge } from '../components/Fingerprint'
import { toTop } from '../scroll'
import { BoardRating, Coverage } from '../components/BoardHealth'
import { Sheet } from '../components/Sheet'

/*
  Board configuration (PERSONA SPEC §7, UI SPEC §11).

  Three decisions the spec is emphatic about, and the reasons they matter:

  1. Words are the label, the number is a footnote. An owner moving a slider is building a
     person, not filling in a form. Every readout string here comes from the server so the
     wording the owner reads is the wording the member is actually given.

  2. Stature, then competence, then disposition. Stature is three sliders and establishes
     who this is quickly. Disposition goes last because it is the most abstract, and by
     then the owner has built the intuition to handle it.

  3. Competence is budgeted and disposition is not. That difference is the whole model
     (§0), so it is rendered rather than explained: competence fills from the left like a
     level, disposition fills from the centre like a deviation.
*/

interface Props {
  members: BoardMember[]
  onSaved: () => void
}

export function BoardConfig({ members, onSaved }: Props) {
  const [catalog, setCatalog] = useState<AttributeCatalog | null>(null)
  const [presets, setPresets] = useState<PersonaPreset[]>([])
  const [validation, setValidation] = useState<BoardValidation | null>(null)
  const [editing, setEditing] = useState<string | null>(null)

  /*
    Opening a seat does not change the shell's screen, so App's reset never fires for it.
    Open the fourth seat from a scrolled roster and the editor arrives already past the
    portrait, which is the first thing on it worth seeing.
  */
  useEffect(() => { toTop() }, [editing])

  useEffect(() => {
    void (async () => {
      const [c, p] = await Promise.all([endpoints.attributes(), endpoints.presets()])
      setCatalog(c)
      setPresets(p)
    })()
  }, [])

  // Re-read after every save. The findings are about the board, so one seat's edit can
  // clear or raise a note that belongs to a different seat entirely.
  const revalidate = useCallback(async () => {
    setValidation(await endpoints.validation())
  }, [])

  useEffect(() => { void revalidate() }, [revalidate, members])

  const seat = members.find(m => m.id === editing) ?? null

  if (seat && catalog) {
    return (
      <SeatEditor
        seat={seat}
        others={members.filter(x => x.id !== seat.id)}
        catalog={catalog}
        presets={presets}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); onSaved(); void revalidate() }}
      />
    )
  }

  return (
    <>
      <h1 className="screen-title">Your board</h1>
      <p className="screen-sub">{members.length} seats. Each one argues from somewhere different.</p>

      {/*
        The board's own checks, drawn.

        The findings block below still carries the sentences, because a warning worth giving
        is worth giving in words. What it could never do is say anything when the board was
        fine: the owner learned their board passed only from the absence of complaint. Eight
        marks say it either way, and they are BoardValidator's own eight rather than a score
        invented for the picture.
      */}
      {validation && <BoardRating findings={validation.findings} />}

      {validation && validation.findings.length > 0 && (
        <div className="findings">
          {validation.findings.map(f => <Finding key={f.check} finding={f} />)}
        </div>
      )}

      {catalog && members.length > 0 && (
        <div className="card">
          <div className="card__title">Where the depth is</div>
          <p className="dim" style={{ margin: '0 0 var(--space-3)' }}>
            Every discipline, and which seats carry it. A short bar is the useful one.
          </p>
          <Coverage members={members} definitions={catalog.definitions} />
        </div>
      )}

      {/*
        Five named people, rendered as five named people.

        This screen is the premise of the product and it used to be the least designed thing
        in it: grey rows, 40px faces, a bare chevron, and none of the seat colours that are
        defined in the tokens and stored on every member. The portrait now carries the seat's
        own hue as a ring, the chair and the dissenter wear the role rather than having it
        appended to a sentence, and the row says what pressing it does.
      */}
      <div className="card card--rows seats">
        {members.map(m => (
          <button
            key={m.id}
            className="seat"
            style={ramp(m.colorRamp)}
            onClick={() => setEditing(m.id)}
          >
            <Avatar member={m} size="xl" />

            <span className="seat__body">
              <span className="seat__head">
                <span className="seat__name">{m.displayName}</span>
                {seatChip(m.seatRole) && (
                  <span className="seat__chip">{seatChip(m.seatRole)}</span>
                )}
                {m.isCustom && <span className="seat__tuned">tuned</span>}
              </span>

              <span className="seat__role">{roleLabel(m.seatRole, m.archetype)}</span>

              <span className="seat__summary">
                {catalog
                  ? summarize(m.attributes, catalog)
                  : <span className="skeleton" style={{ width: 220 }} />}
              </span>
            </span>

            {/* "Tune" rather than a lone chevron. Nothing on the old row said a seat could
                be changed, which is the entire reason this screen exists. */}
            {/* The shape of how this seat argues, in its own colour. Two rows next to each
                other and the difference between the seats is visible before anything is
                read, which is the whole premise of having five of them. */}
            {catalog && (
              <Fingerprint
                attributes={m.attributes}
                definitions={catalog.definitions}
                size={52}
                web={false}
              />
            )}

            <span className="seat__go">
              Tune
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </span>
          </button>
        ))}
      </div>

      {members.length === 0 && (
        <div className="empty"><div className="empty__title">No board configured</div></div>
      )}
    </>
  )
}

/*
  Two lines of plain language, never raw numbers (UI SPEC §11).

  Built from the strongest deviations rather than from a fixed set of attributes: a seat
  that is unremarkable on risk and extreme on contrarianism should be described by the
  contrarianism. A member sitting at mid on everything genuinely has nothing to say here,
  and saying so is more honest than padding it.
*/
function summarize(attributes: PersonaAttributes, catalog: AttributeCatalog): string {
  const byKey = new Map(catalog.definitions.map(d => [d.key, d]))

  const traits = Object.entries(attributes.disposition)
    .map(([key, value]) => ({ key, value, distance: Math.abs(value - 5) }))
    .filter(t => t.distance > 1 && byKey.has(t.key))
    .sort((a, b) => b.distance - a.distance || a.key.localeCompare(b.key))
    .slice(0, 3)
    .map(t => readoutFor(byKey.get(t.key)!, t.value).toLowerCase())
    .filter(Boolean)

  const strongest = Object.entries(attributes.competence)
    .sort((a, b) => b[1] - a[1])[0]

  const depth = strongest && strongest[1] >= 7 && byKey.has(strongest[0])
    ? `Deep on ${byKey.get(strongest[0])!.displayName.toLowerCase()}.`
    : ''

  if (traits.length === 0) return depth || 'Balanced across the board.'
  return `${sentence(traits.join(', '))}. ${depth}`.trim()
}

function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/*
  A composition note, said the way a board member would say it (§5).

  Rendered as prose with no heading, no icon and no "warning" label. The moment this reads
  as form validation the owner skims past it, and the whole point is that these are the
  observations that decide whether the subscription is worth keeping.
*/
function Finding({ finding }: { finding: BoardFinding }) {
  return (
    <p className={`finding ${finding.isBlocking ? 'finding--blocking' : ''}`}>
      {finding.message}
    </p>
  )
}

// ─── Seat editor ────────────────────────────────────────────────────────────

interface EditorProps {
  seat: BoardMember

  /** The other four, so a slider can show where this seat sits against them. */
  others: BoardMember[]
  catalog: AttributeCatalog
  presets: PersonaPreset[]
  onClose: () => void
  onSaved: () => void
}

function SeatEditor({ seat, others, catalog, presets, onClose, onSaved }: EditorProps) {
  const [name, setName] = useState(seat.displayName)
  const [archetype, setArchetype] = useState(seat.archetype)
  const [backstory, setBackstory] = useState(seat.backstory ?? '')
  const [domains, setDomains] = useState(seat.domains.join(', '))
  const [attributes, setAttributes] = useState<PersonaAttributes>(seat.attributes)
  const [avatarUrl, setAvatarUrl] = useState(seat.avatarUrl)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [picking, setPicking] = useState(false)

  const groups = useMemo(() => ({
    stature:     ordered(catalog, 'stature'),
    competence:  ordered(catalog, 'competence'),
    disposition: ordered(catalog, 'disposition'),
  }), [catalog])

  const spent = competenceTotal(attributes)
  const remaining = catalog.competenceBudget - spent

  const set = (group: keyof PersonaAttributes, key: string, value: number) => {
    setAttributes(a => ({ ...a, [group]: { ...a[group], [key]: value } }))
    setError(null)
  }

  const applyPreset = (preset: PersonaPreset) => {
    setName(preset.displayName)
    setArchetype(preset.archetype)
    setBackstory(preset.backstory ?? '')
    setDomains(preset.domains.join(', '))
    setAttributes(preset.attributes)
    // The face travels with the persona. A seat re-cast as somebody else that kept the old
    // portrait would read as the product not noticing.
    setAvatarUrl(preset.avatarUrl)
    setError(null)
  }

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await endpoints.saveSeat(seat.id, {
        displayName: name.trim(),
        archetype: archetype.trim() || null,
        attributes,
        domains: domains.split(',').map(d => d.trim()).filter(Boolean),
        backstory: backstory.trim() || null,
        customPrompt: null,
        avatarUrl,
      })
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save')
    } finally {
      setSaving(false)
    }
  }

  /*
    Two columns.

    The editor was a single 672px column 4,096 pixels tall: nineteen sliders stacked one to
    a row, the competence budget scrolled out of sight by the fifth of them, and Save four
    thousand pixels down and static. Most of the page was empty on either side of it.

    The rail holds who this is, where to jump, how the seat currently reads, and the two
    buttons; it stays put. The body holds everything you change, and its sliders sit two to
    a row. Below 1080px it collapses back to one column in the same order.
  */
  return (
    <>
      <button className="btn btn--quiet editor__back" onClick={onClose}>‹ Back to the board</button>

      {error && <div className="signin__error">{error}</div>}

      <div className="editor" style={ramp(seat.colorRamp)}>
        <aside className="editor__rail">
          <div className="editor__id">
            <Avatar member={{ ...seat, displayName: name, avatarUrl }} size="xl" />
            <div>
              <h1 className="editor__name">{name || 'This seat'}</h1>
              <p className="editor__arch">{archetype || 'no role yet'}</p>
            </div>
          </div>

          {/* What the Board screen prints for this seat, updating as the sliders move. The
              owner is building a sentence, so the sentence is on screen while they build it. */}
          <div className="editor__reads">
            <span className="editor__readslabel">Reads as</span>
            <p>{summarize(attributes, catalog)}</p>
          </div>

          <nav className="editor__jump">
            <a href="#seat-who">Who they are</a>
            <a href="#seat-standing">Standing</a>
            <a href="#seat-know">What they know</a>
            <a href="#seat-think">How they think</a>
          </nav>

          <div className="editor__save">
            <button className="btn btn--primary" disabled={saving || !name.trim()} onClick={save}>
              {saving ? 'Saving' : 'Save this seat'}
            </button>
            <button className="btn btn--quiet" onClick={onClose}>Discard</button>
          </div>
        </aside>

        <div className="editor__body">
          {/*
            Most owners never go past a preset, and that is the intended outcome (UI SPEC
            §11) — but eight portrait cards took 372 pixels at the top of the page for
            something pressed once in a seat's life, and it warns you it overwrites
            everything below it. One button, and the same sheet Settings uses.
          */}
          <div className="editor__preset">
            <span className="dim">
              Everything here changes what this member is told before every meeting. Nothing
              here is shown to them as a number.
            </span>
            <button className="btn" onClick={() => setPicking(true)}>Start from someone</button>
          </div>

          {picking && (
            <Sheet title="Start from someone" onClose={() => setPicking(false)}>
              <p className="muted" style={{ marginBottom: 'var(--space-4)' }}>
                Applying one of these replaces every setting on this seat, including the name.
              </p>
              <div className="presets">
                {presets.map(p => (
                  <button
                    key={p.key}
                    className="preset"
                    onClick={() => { applyPreset(p); setPicking(false) }}
                  >
                    {p.avatarUrl && <img className="preset__face" src={p.avatarUrl} alt="" loading="lazy" />}
                    <span>
                      <span className="preset__name">{p.displayName}</span>
                      <span className="preset__archetype">{p.archetype}</span>
                    </span>
                  </button>
                ))}
              </div>
            </Sheet>
          )}

      <div className="card" id="seat-who">
        <div className="card__title" style={{ marginBottom: 'var(--space-3)' }}>Who they are</div>

        <label className="field">
          <span className="field__label">Name</span>
          <input className="input" value={name} onChange={e => setName(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">How they introduce themselves</span>
          <input className="input" value={archetype} placeholder="fractional CFO"
                 onChange={e => setArchetype(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">Background</span>
          <textarea className="textarea" value={backstory} rows={2}
                    placeholder="Closed books for forty owner-operated businesses."
                    onChange={e => setBackstory(e.target.value)} />
          <span className="dim">One or two lines. This is read out loud in their voice.</span>
        </label>

        <label className="field">
          <span className="field__label">Credible on</span>
          {/*
            A textarea rather than an input, because this is a list and lists get long. On a
            phone a real seat reads "strategy, cash flow, crisis, pricing, compe" and the rest
            is off the right-hand edge, so the one field whose whole job is to show you what
            the chair weights was the one field you could not read.
          */}
          <textarea className="textarea" rows={2} value={domains}
                    placeholder="finance, margin, cash flow"
                    onChange={e => setDomains(e.target.value)} />
          <span className="dim">Comma separated. The chair weights their vote on these topics.</span>
        </label>
      </div>

      <Group
        id="seat-standing"
        title="Standing"
        blurb="How much they have seen, and at what size. This is what stops advice built for a
               company ten times yours."
        definitions={groups.stature}
        attributes={attributes}
        group="stature"
        onChange={set}
      />

      <div className="card" id="seat-know">
        <div className="card__head card__head--sticky">
          <div>
            <div className="card__title">What they know</div>
            <p className="dim" style={{ margin: '2px 0 0' }}>
              Depth is finite. A member who knows everything argues like nobody.
            </p>
          </div>
          <div className={`budget ${remaining === 0 ? 'budget--spent' : ''}`}>
            <span className="budget__value">{remaining}</span>
            <span className="budget__label">of {catalog.competenceBudget} left</span>
          </div>
        </div>

        {remaining === 0 && (
          <p className="budget__prompt">
            This seat is at their limit. To make them stronger somewhere, take it from
            somewhere else.
          </p>
        )}

        {/*
          Ten of these stacked, with the poles dropped and the fill in the seat's own colour,
          read as a bar chart rather than ten controls. Still a range input underneath, so it
          keeps its keyboard handling and its screen reader announcement: a hand-drawn bar
          with a drag handler would have neither.
        */}
        <div className="sliders sliders--bars">
          {groups.competence.map(d => (
            <Slider
              key={d.key}
              definition={d}
              compact
              peers={others.map(o => o.attributes.competence[d.key] ?? 0)}
              value={attributes.competence[d.key] ?? 0}
              // Clamped, not rescaled. Lowering the input's own max would put the thumb
              // at the right-hand end of a shortened track while the fill still drew on
              // the real 0-10 scale, so a seat at 6 of 10 looked maxed out.
              cap={(attributes.competence[d.key] ?? 0) + remaining}
              dimmed={remaining === 0 && (attributes.competence[d.key] ?? 0) === 0}
              onChange={v => set('competence', d.key, v)}
            />
          ))}
        </div>
      </div>

      <Group
        id="seat-think"
        title="How they think"
        blurb="Neither end is better. These are the differences that make five seats worth
               more than one."
        definitions={groups.disposition}
        attributes={attributes}
        group="disposition"
        peers={others.map(o => o.attributes.disposition)}
        chart={
          <FingerprintLarge
            attributes={attributes}
            definitions={catalog.definitions}
            size={210}
          />
        }
        onChange={set}
        bidirectional
      />

        </div>
      </div>
    </>
  )
}

function Group({
  id, title, blurb, definitions, attributes, group, onChange, bidirectional, peers, chart,
}: {
  id: string
  title: string
  blurb: string
  definitions: AttributeDefinition[]
  attributes: PersonaAttributes
  group: keyof PersonaAttributes
  onChange: (group: keyof PersonaAttributes, key: string, value: number) => void
  bidirectional?: boolean
  /** The same group on the other seats, so each track can show where they sit. */
  peers?: Record<string, number>[]
  /** Drawn above the sliders. The disposition group passes its own fingerprint. */
  chart?: React.ReactNode
}) {
  return (
    <div className="card" id={id}>
      <div className="card__title">{title}</div>
      <p className="dim" style={{ margin: '2px 0 var(--space-4)' }}>{blurb}</p>

      {/* The shape first, then the seven controls that make it. Somebody changing bluntness
          can watch the outline move rather than reading a number back. */}
      {chart}

      <div className="sliders">
        {definitions.map(d => (
          <Slider
            key={d.key}
            definition={d}
            peers={peers?.map(row => row[d.key] ?? 5)}
            value={attributes[group][d.key] ?? 5}
            bidirectional={bidirectional}
            onChange={v => onChange(group, d.key, v)}
          />
        ))}
      </div>
    </div>
  )
}

function Slider({
  definition, value, onChange, bidirectional, cap = 10, dimmed, compact, peers,
}: {
  definition: AttributeDefinition
  value: number
  onChange: (value: number) => void
  bidirectional?: boolean
  /** Highest value the budget still allows. Dragging past it simply stops. */
  cap?: number
  dimmed?: boolean
  /** Drops the poles line and thickens the track, so a stack of them reads as a chart. */
  compact?: boolean
  /**
   * The same attribute on the other seats.
   *
   * The reference this came from put a histogram behind the track as decoration. Here there
   * is something real to put there: a seat is only strong or blunt relative to the four
   * people it argues with, and tuning one in isolation is how a board ends up with five
   * seats that all know finance and none that know the trade.
   */
  peers?: number[]
}) {
  const pct = value * 10

  // Centre-origin for disposition, left-origin for competence and stature. At mid the
  // bidirectional fill is zero width, which is the correct picture: mid contributes
  // nothing to the prompt at all (§2.1).
  const fill = bidirectional
    ? { left: `${Math.min(pct, 50)}%`, width: `${Math.abs(pct - 50)}%` }
    : { left: 0, width: `${pct}%` }

  return (
    <div className={`slider${dimmed ? ' slider--dimmed' : ''}${compact ? ' slider--compact' : ''}`}>
      {/*
        The readout sits beside the name rather than on a line of its own. It was costing
        twenty-six pixels a row across nineteen rows for a word that fits next to the label.
        The word is still the label and the number is still the footnote, which is what
        §11 asks for.
      */}
      <div className="slider__head">
        <span className="slider__name">{definition.displayName}</span>
        <span className="slider__readout">{readoutFor(definition, value) || 'Balanced'}</span>
        <span className="slider__number">{value}</span>
      </div>

      <div className={`slider__track ${bidirectional ? 'slider__track--centred' : ''}`}>
        {/* Behind the fill, so this seat's own value always reads on top of the others. */}
        {peers?.map((p, i) => (
          <span key={i} className="slider__peer" style={{ left: `${p * 10}%` }} />
        ))}

        <span className="slider__fill" style={fill} />
        <input
          className="slider__input"
          type="range"
          min={0}
          max={10}
          step={1}
          value={value}
          aria-label={definition.displayName}
          aria-valuetext={readoutFor(definition, value)}
          onChange={e => onChange(Math.min(Number(e.target.value), cap))}
        />
      </div>

      {!compact && (
        <div className="slider__poles">
          <span>{definition.poleLow}</span>
          <span>{definition.poleHigh}</span>
        </div>
      )}
    </div>
  )
}

function ordered(catalog: AttributeCatalog, category: string): AttributeDefinition[] {
  return catalog.definitions
    .filter(d => d.category === category)
    .sort((a, b) => a.sortOrder - b.sortOrder)
}

/*
  The chip says chair, so the role line no longer has to. The dissenter keeps its clause,
  because "argues against the consensus by mandate" is the answer to the question a new
  owner actually has, which is why there is somebody on the board who disagrees on purpose.
*/
function roleLabel(role: string, archetype: string): string {
  if (role === 'dissenter') return `${archetype} · argues against the consensus by mandate`
  return archetype
}

function seatChip(role: string): string | null {
  if (role === 'chair') return 'Chair'
  if (role === 'dissenter') return 'Dissent'
  return null
}


